import React, { MouseEvent, useState } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Styles } from '@t2c/shared-fe';
import { Iconify, NextImage } from '@sdf/base';
import { DropdownPopover } from './DropdownPopover';
import { DropdownMenuAction } from '../../@types';
import ReactPlayer from 'react-player';
import { useAudioPlayer, usePopover } from '../../hooks';
import IconButton from '@mui/material/IconButton';
import { extractDate, extractTime } from '../../utils';

interface AudioCardProps {
  data: any;
  audioUrl: string;
  title?: string;
  menuActions: DropdownMenuAction[];
  isErrorStatus: boolean;
  isInProgressStatus: boolean;
  isSmallSize?: boolean;
  isActive?: boolean;
}

export const AudioCard: React.FC<AudioCardProps> = ({
  data,
  audioUrl,
  title,
  menuActions,
  isErrorStatus,
  isInProgressStatus,
  isSmallSize,
  isActive = false,
}) => {
  const {
    isSeekBarVisible,
    isPlaying,
    playbackPosition,
    togglePlayPause,
    handleSeekChange,
    handleProgress,
    handleEnded,
    playerRef,
  } = useAudioPlayer();

  const { popoverAnchor, handleOpenPopover, handleClosePopover } = usePopover();

  return (
    <Card sx={{
      ...styles.card,
      ...(isActive && {
        borderColor: 'text.coloredText',
        borderWidth: 2,
      }),
    }}>
      {!!menuActions.length && !isErrorStatus && !isInProgressStatus && (
        <Box sx={styles.menuButton} onClick={handleOpenPopover}>
          <Iconify
            className="audio-menu-icon"
            icon="solar:menu-dots-bold"
            fontSize={'22px'}
            sx={styles.menuIcon}
          />
        </Box>
      )}

      <Stack p={2} mt={2}>
        <Box mb={0.5} alignSelf={'center'}>
          <NextImage
            src={isPlaying ? '/assets/equiliser.gif' : '/assets/still_equiliser.png'}
            alt="Audio Visual"
            width={600}
            height={40}
          />
        </Box>
        <Stack sx={styles.playerControls}>
          <Box>
            <IconButton onClick={togglePlayPause} sx={{ p: 0 }} disabled={isInProgressStatus}>
              <Iconify
                icon={isPlaying ? 'fa-solid:pause-circle' : 'fa-solid:play-circle'}
                width={isSmallSize ? 30 : 36}
                height={isSmallSize ? 30 : 36}
                sx={styles.playPauseIcon}
              />
            </IconButton>
          </Box>
          {isSeekBarVisible && (
            <Slider
              value={playbackPosition * 100}
              onChange={handleSeekChange}
              aria-labelledby="audio-slider"
              sx={styles.slider}
            />
          )}
        </Stack>
      </Stack>

      {(isErrorStatus || isInProgressStatus) && (
        <Stack sx={styles.statusWrapper}>
          <Box
            sx={{
              ...styles.statusContainer,
              ...(isErrorStatus && {
                border: '1px solid',
                background: (theme) => theme.palette.text.primary,
                borderColor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.error.main,
              }),
              ...(isInProgressStatus && {
                background: (theme) => theme.palette.gradients.gradient1,
                color: (theme) => theme.palette.text.primary,
              }),
            }}
          >
            <Typography>
              {isErrorStatus && 'Error'}
              {isInProgressStatus && 'In Progress...'}
            </Typography>
          </Box>
        </Stack>
      )}

      {title && (<Box className="audio-title-container" sx={styles.titleContainer}>
        <Tooltip title={title}>
          <Typography sx={styles.title}>{title?.length > 15 ? `${title.slice(0, 15)}...` : title}</Typography>
        </Tooltip>
        <Typography sx={{fontSize: 12,textAlign: 'right'}}>
          {extractDate(data?.updatedAt)}
          &nbsp;
          {extractTime(data?.updatedAt)}
        </Typography>
      </Box>)}

      <ReactPlayer
        ref={playerRef}
        url={audioUrl}
        playing={isPlaying}
        onProgress={handleProgress}
        onEnded={handleEnded}
        height="0"  
        width="0"
        onError={(error, data) => {
          console.error('Audio playback error:', error, data);
        }}
        progressInterval={100} // Adjust as needed
      />
      <DropdownPopover
        anchorElement={popoverAnchor}
        menuActions={menuActions}
        onClose={handleClosePopover}
        data={data}
      />
    </Card>
  );
};

const styles: Styles = {
  card: {
    width: '100%',
    cursor: 'default',
    zIndex: 'auto',
    border: '1px solid',
    overflow: 'hidden',
    borderColor: (theme) => theme.palette.background.border,
    background: (theme) => theme.palette.background.secondary,
    position: 'relative',
    color: (theme) => theme.palette.text.primary,
    '&:hover': {
      background: (theme) => theme.palette.background.primary,
      color: (theme) => theme.palette.text.primary,
      border: (theme) => `1px solid ${theme.palette.background.border}`,
      '.audio-title-container': {
        background: (theme) => theme.palette.background.activeBorder,
      },
    },
  },
  menuButton: {
    // backgroundColor: '#fff',
    borderRadius: '4px',
    position: 'absolute',
    top: '10px',
    right: '8px',
    padding: '4px',
    cursor: 'pointer',

  },
  playPauseIcon: {
    color: (theme) => theme.palette.background.activeBorder,
    // borderRadius: 1000,
    cursor: 'pointer',
  },
  playerControls: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 2,
  },
  slider: {
    width: '100%',
    height: 8,
    '& .MuiSlider-thumb': {
      width: 20,
      height: 20,
      backgroundColor: '#fff',
      '&:before': {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      },
    },
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: (theme) => theme.palette.background.activeBorder,
    },
    '& .MuiSlider-rail': {
      opacity: 1,
      backgroundColor: '#fff',
    },
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0.8,
  },
  title: {
    fontSize: 14,
    fontWeight: 400,
    maxWidth: '50%',
    textAlign: 'left',
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  menuIcon: {
    color: (theme) => theme.palette.text.primary,
  },
  statusWrapper: {
    position: 'absolute',
    width: '100%',
    top: '50%',
    alignItems: 'center',
  },
  statusContainer: {
    maxWidth: 300,
    padding: 0.3,
    paddingX: 2,
    borderRadius: 25,
  },
};
